'use client';
import React, { useState } from 'react';
import { Cpu, MapPin, AlertTriangle, CheckCircle, Clock, Zap, Eye, Plus } from 'lucide-react';

interface UnregisteredProperty {
  id: string;
  discoveryMethod: 'ai-satellite' | 'field-report' | 'utility-data' | 'community-tip';
  county: string;
  approximateAddress: string;
  coordinates: string;
  estimatedArea: string;
  estimatedLandUse: 'residential' | 'commercial' | 'agricultural' | 'industrial';
  confidenceScore: number;
  discoveredDate: string;
  status: 'detected' | 'field-verified' | 'assessment-pending' | 'registered';
  estimatedValue?: number;
  estimatedTax?: number;
  notes: string;
}

const PIPELINE: UnregisteredProperty[] = [
  {
    id: 'URP-2024-0041', discoveryMethod: 'ai-satellite', county: 'Nimba', approximateAddress: 'Ganta Market Zone B — Ext.',
    coordinates: '7.2312°N, 8.9901°W', estimatedArea: '0.85 ha', estimatedLandUse: 'commercial',
    confidenceScore: 94, discoveredDate: '2024-04-02', status: 'field-verified',
    estimatedValue: 95000, estimatedTax: 2565, notes: 'AI detected 3 new commercial structures via satellite imagery. Field officer confirmed on 2024-04-12.',
  },
  {
    id: 'URP-2024-0042', discoveryMethod: 'utility-data', county: 'Montserrado', approximateAddress: 'Old Road, Paynesville Ext.',
    coordinates: '6.3201°N, 10.7612°W', estimatedArea: '0.22 ha', estimatedLandUse: 'residential',
    confidenceScore: 87, discoveredDate: '2024-04-05', status: 'assessment-pending',
    estimatedValue: 68000, estimatedTax: 1530, notes: 'Electricity connection detected at unregistered address. No parcel ID found in registry.',
  },
  {
    id: 'URP-2024-0043', discoveryMethod: 'ai-satellite', county: 'Margibi', approximateAddress: 'Kakata Bypass Road',
    coordinates: '6.5412°N, 10.3521°W', estimatedArea: '1.40 ha', estimatedLandUse: 'industrial',
    confidenceScore: 91, discoveredDate: '2024-04-08', status: 'detected',
    estimatedValue: 180000, estimatedTax: 5400, notes: 'Large warehouse structure detected. No registration record. High-value industrial property.',
  },
  {
    id: 'URP-2024-0044', discoveryMethod: 'community-tip', county: 'Bong', approximateAddress: 'Gbarnga Outskirts, Plot 7A',
    coordinates: '7.0021°N, 9.4812°W', estimatedArea: '3.20 ha', estimatedLandUse: 'agricultural',
    confidenceScore: 72, discoveredDate: '2024-04-10', status: 'detected',
    estimatedValue: 45000, estimatedTax: 540, notes: 'Community tip about undeclared farmland. Satellite confirms active cultivation.',
  },
  {
    id: 'URP-2024-0045', discoveryMethod: 'ai-satellite', county: 'Grand Bassa', approximateAddress: 'Buchanan Industrial Zone B',
    coordinates: '5.8901°N, 10.0312°W', estimatedArea: '2.10 ha', estimatedLandUse: 'industrial',
    confidenceScore: 96, discoveredDate: '2024-03-28', status: 'registered',
    estimatedValue: 320000, estimatedTax: 9600, notes: 'Successfully registered after AI discovery. First-time assessment completed. Tax bill issued.',
  },
  {
    id: 'URP-2024-0046', discoveryMethod: 'field-report', county: 'Sinoe', approximateAddress: 'Greenville Coastal Strip',
    coordinates: '5.0201°N, 9.0412°W', estimatedArea: '1.80 ha', estimatedLandUse: 'commercial',
    confidenceScore: 88, discoveredDate: '2024-04-15', status: 'field-verified',
    estimatedValue: 142000, estimatedTax: 3834, notes: 'Field officer identified during delinquency patrol. Fishing operation with no tax registration.',
  },
];

const discoveryConfig = {
  'ai-satellite': { color: '#a78bfa', bg: 'rgba(167,139,250,0.1)', label: 'AI Satellite', icon: Cpu },
  'field-report': { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Field Report', icon: Eye },
  'utility-data': { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: 'Utility Data', icon: Zap },
  'community-tip': { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'Community Tip', icon: AlertTriangle },
};

const statusConfig = {
  detected: { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Detected', icon: AlertTriangle },
  'field-verified': { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: 'Field Verified', icon: Clock },
  'assessment-pending': { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Assessment Pending', icon: Clock },
  registered: { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'Registered', icon: CheckCircle },
};

const landUseColors = {
  residential: '#60a5fa', commercial: '#f59e0b', agricultural: '#34d399', industrial: '#a78bfa',
};

export default function UnregisteredPropertyPipelinePanel() {
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterMethod, setFilterMethod] = useState<string>('all');

  const filtered = PIPELINE.filter((p) => {
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchMethod = filterMethod === 'all' || p.discoveryMethod === filterMethod;
    return matchStatus && matchMethod;
  });

  const totalEstimatedTax = PIPELINE.filter((p) => p.estimatedTax).reduce((s, p) => s + (p.estimatedTax || 0), 0);
  const formatCurrency = (val: number) => `$${val.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Detected', value: PIPELINE.length, color: '#a78bfa', sub: 'Unregistered properties' },
          { label: 'Awaiting Field Verify', value: PIPELINE.filter((p) => p.status === 'detected').length, color: '#f87171', sub: 'Need inspection' },
          { label: 'Ready to Register', value: PIPELINE.filter((p) => p.status === 'field-verified').length, color: '#fbbf24', sub: 'Verified, not yet registered' },
          { label: 'Est. Tax Revenue', value: formatCurrency(totalEstimatedTax), color: '#34d399', sub: 'If all registered' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
            <p className="text-xl font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Status:</span>
          {['all', 'detected', 'field-verified', 'assessment-pending', 'registered'].map((f) => (
            <button
              key={f}
              onClick={() => setFilterStatus(f)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize"
              style={{
                backgroundColor: filterStatus === f ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                color: filterStatus === f ? 'var(--accent)' : 'var(--dark-muted)',
                border: `1px solid ${filterStatus === f ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
              }}
            >
              {f === 'all' ? 'All' : f.replace('-', ' ')}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Source:</span>
          {['all', 'ai-satellite', 'field-report', 'utility-data', 'community-tip'].map((f) => (
            <button
              key={f}
              onClick={() => setFilterMethod(f)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: filterMethod === f ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                color: filterMethod === f ? 'var(--accent)' : 'var(--dark-muted)',
                border: `1px solid ${filterMethod === f ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
              }}
            >
              {f === 'all' ? 'All' : f.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Pipeline cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map((prop) => {
          const discCfg = discoveryConfig[prop.discoveryMethod];
          const DiscIcon = discCfg.icon;
          const statCfg = statusConfig[prop.status];
          const StatIcon = statCfg.icon;
          const luColor = landUseColors[prop.estimatedLandUse];

          return (
            <div key={prop.id} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{prop.id}</span>
                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: discCfg.bg, color: discCfg.color }}>
                    <DiscIcon size={10} />
                    {discCfg.label}
                  </span>
                </div>
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: statCfg.bg, color: statCfg.color }}>
                  <StatIcon size={10} />
                  {statCfg.label}
                </span>
              </div>

              <div className="flex items-center gap-1.5 mb-1">
                <MapPin size={11} style={{ color: 'var(--dark-muted)' }} />
                <p className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{prop.approximateAddress}</p>
              </div>
              <p className="text-xs mb-2" style={{ color: 'var(--dark-muted)' }}>{prop.county} · {prop.coordinates}</p>

              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded-full capitalize"
                  style={{ backgroundColor: luColor + '15', color: luColor }}>
                  {prop.estimatedLandUse}
                </span>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{prop.estimatedArea}</span>
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: prop.confidenceScore >= 90 ? 'rgba(52,211,153,0.1)' : 'rgba(251,191,36,0.1)', color: prop.confidenceScore >= 90 ? '#34d399' : '#fbbf24' }}>
                  {prop.confidenceScore}% confidence
                </span>
              </div>

              {(prop.estimatedValue || prop.estimatedTax) && (
                <div className="flex items-center gap-3 mb-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  {prop.estimatedValue && (
                    <div>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Est. Value</p>
                      <p className="text-xs font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{formatCurrency(prop.estimatedValue)}</p>
                    </div>
                  )}
                  {prop.estimatedTax && (
                    <div>
                      <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Est. Annual Tax</p>
                      <p className="text-xs font-bold font-mono-data" style={{ color: '#34d399' }}>{formatCurrency(prop.estimatedTax)}</p>
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--dark-muted)' }}>{prop.notes}</p>

              {prop.status !== 'registered' && (
                <button className="w-full py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
                  <Plus size={11} />
                  {prop.status === 'detected' ? 'Assign Field Inspection' : prop.status === 'field-verified' ? 'Begin Registration' : 'Complete Assessment'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
